import { esc, fmtMoney, fmtNum } from "./format.js";
import { sfx } from "../app/audio.js";

// ---------------------------------------------------------------------
// Investment slider modal — replaces the plain number-input modal for
// every "how much do you want to invest?" decision (R&D, Marketing,
// Capex, Automation, Maintenance, Factory build/expand, Raise Capital,
// Loan, Pay Debt, Dividend). The player drags a slider bounded by what
// they can actually afford, and a live effect panel below updates on
// every drag tick using the *exact same formulas* engine.py runs when
// the command is actually submitted — so the preview is never a lie,
// just a client-side mirror of server-side math that gets overwritten
// by the real numbers the moment the command posts.
//
// config = {
//   title, description,            // header text
//   min, max,                      // slider bounds ($ or units)
//   step,                          // slider step
//   default,                       // starting position
//   unit,                          // "$" (default) or "units" etc.
//   capLabel,                      // text under the amount, e.g. "of $1.2M cash available"
//   quickPicks: [ {label, value} ] // preset jump buttons (25%/50%/75%/Max, etc.)
//   computeEffect(amount) -> { stats: [{label, value, cls}], note, noteWarn }
//   confirmLabel,
//   onConfirm(amount) -> Promise
// }
// ---------------------------------------------------------------------
function openInvestSliderModal(config) {
  const {
    title, description, min = 0, max, step = 1000, default: def,
    unit = "$", capLabel, quickPicks, computeEffect, confirmLabel = "Confirm", onConfirm,
  } = config;

  const safeMax = Math.max(min, max || 0);
  const startVal = Math.min(Math.max(def !== undefined ? def : safeMax * 0.2, min), safeMax);

  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  backdrop.innerHTML = `
    <div class="modal" style="width:min(520px, 92vw);">
      <h3>${esc(title)}</h3>
      ${description ? `<div style="font-size:11.5px; color:var(--text-mid); background:var(--bg-1); border:1px solid var(--line); border-radius:var(--radius); padding:10px 12px; margin-bottom:16px; line-height:1.5;">${esc(description)}</div>` : ""}
      <div class="invest-slider-wrap">
        <div class="invest-slider-amount-row">
          <div class="invest-slider-amount" id="islider-amount"></div>
          <div class="invest-slider-cap">${capLabel ? esc(capLabel) : ""}</div>
        </div>
        <input type="range" class="invest-slider" id="islider-range" min="${min}" max="${safeMax}" step="${step}" value="${startVal}" />
        ${quickPicks && quickPicks.length ? `
          <div class="invest-slider-ticks">
            ${quickPicks.map((q, i) => `<button type="button" data-pick="${i}">${esc(q.label)}</button>`).join("")}
          </div>
        ` : ""}
        <input type="hidden" id="islider-exact" value="${startVal}" />
      </div>
      <div class="invest-effect-panel" id="islider-effect"></div>
      <div class="modal-actions">
        <button id="modal-cancel">Cancel</button>
        <button class="primary" id="modal-ok">${esc(confirmLabel)}</button>
      </div>
    </div>
  `;
  document.body.appendChild(backdrop);

  const rangeEl = backdrop.querySelector("#islider-range");
  const exactEl = backdrop.querySelector("#islider-exact");
  const amountEl = backdrop.querySelector("#islider-amount");
  const effectEl = backdrop.querySelector("#islider-effect");

  function renderEffect(amount) {
    amountEl.textContent = unit === "$" ? fmtMoney(amount) : unit === "%" ? `${amount}%` : `${fmtNum(amount)} ${unit}`;
    const effect = computeEffect ? computeEffect(amount) : { stats: [] };
    effectEl.innerHTML = `
      <div class="invest-effect-title">Projected Effect</div>
      <div class="invest-effect-grid">
        ${(effect.stats || []).map((s) => `
          <div>
            <div class="invest-effect-stat-label">${esc(s.label)}</div>
            <div class="invest-effect-stat-value ${s.cls || ""}">${esc(s.value)}</div>
          </div>
        `).join("")}
      </div>
      ${effect.note ? `<div class="invest-effect-note ${effect.noteWarn ? "warn" : ""}">${esc(effect.note)}</div>` : ""}
    `;
  }

  function setAmount(v) {
    v = Math.min(Math.max(parseFloat(v) || 0, min), safeMax);
    rangeEl.value = v;
    exactEl.value = v;
    renderEffect(v);
  }

  rangeEl.addEventListener("input", () => setAmount(rangeEl.value));
  if (quickPicks && quickPicks.length) {
    backdrop.querySelectorAll("[data-pick]").forEach((btn) => {
      btn.addEventListener("click", () => setAmount(quickPicks[parseInt(btn.dataset.pick)].value));
    });
  }

  renderEffect(startVal);

  backdrop.querySelector("#modal-cancel").addEventListener("click", () => backdrop.remove());
  backdrop.querySelector("#modal-ok").addEventListener("click", async () => {
    const amount = parseFloat(exactEl.value) || 0;
    backdrop.remove();
    if (amount <= 0) return;
    await onConfirm(amount);
  });
}

// ---------------------------------------------------------------------
// Sliders everywhere: no typed amounts. sliderField() renders a range with
// a live readout; one document-level listener keeps every readout current.
// ---------------------------------------------------------------------
function cashNow() {
  const s = window.__snapshot;
  return (s && s.balance_sheet && s.balance_sheet.CASH) || 0;
}

/** A round step (1, 2 or 5 x 10^k) giving about 200 stops up to max. */
function niceStep(max, floor = 1) {
  const raw = Math.max(floor, max / 200);
  const p = Math.pow(10, Math.floor(Math.log10(raw)));
  const m = raw / p;
  return Math.max(floor, (m <= 1 ? 1 : m <= 2 ? 2 : m <= 5 ? 5 : 10) * p);
}

/** A money slider's top: room above the default, and up to the cash you hold. */
function moneySliderMax(def) {
  const top = Math.max((def || 100000) * 4, cashNow());
  const step = niceStep(top, 1000);
  return Math.ceil(top / step) * step;
}

function sliderText(v, unit) {
  const n = +v;
  if (unit === "money") return fmtMoney(n);
  if (unit === "pct") return `${n}%`;
  if (unit === "months") return `${n} month${n === 1 ? "" : "s"}`;
  return `${fmtNum(n)} ${unit}`;
}

function sliderField(id, label, { min = 0, max, step, value, unit = "money" }) {
  const st = step || (unit === "money" ? niceStep(max, 1000) : 1);
  const v = Math.min(max, Math.max(min, value));
  return `<div class="field slider-field">
    <div class="slider-field-head"><label for="${id}">${esc(label)}</label><b id="${id}-val">${esc(sliderText(v, unit))}</b></div>
    <input type="range" class="invest-slider" id="${id}" min="${min}" max="${max}" step="${st}" value="${v}" data-readout="${id}-val" data-unit="${esc(unit)}" />
    <div class="slider-field-scale"><span>${esc(sliderText(min, unit))}</span><span>${esc(sliderText(max, unit))}</span></div>
  </div>`;
}

document.addEventListener("input", (e) => {
  const t = e.target;
  if (!t || !t.dataset || !t.dataset.readout) return;
  const out = document.getElementById(t.dataset.readout);
  if (out) out.textContent = sliderText(t.value, t.dataset.unit);
});

/** The generic "how much?" prompt, as a slider with its effect shown live.
 *  Percentages run 1-50, months 1-24, money up to the cash you hold. */
function openAmountModal(title, label, onSubmit, isPct, defaultVal, contextInfo) {
  const pct = isPct || /%/.test(label);
  const months = !pct && /months?\b/i.test(label);
  const borrowing = /borrow/i.test(title + label);
  const cash = cashNow();
  let cfg;
  if (pct) cfg = { min: 1, max: 50, step: 1, default: defaultVal || 20, unit: "%" };
  else if (months) cfg = { min: 1, max: 24, step: 1, default: defaultVal || 3, unit: "months" };
  else {
    const max = moneySliderMax(defaultVal);
    const step = niceStep(max, 1000);
    cfg = {
      min: step, max, step, default: defaultVal || 100000, unit: "$",
      capLabel: borrowing ? "" : `of ${fmtMoney(cash)} cash`,
      quickPicks: borrowing ? [] : [0.1, 0.25, 0.5].map((f) => ({ label: `${f * 100}% of cash`, value: Math.max(step, Math.round((cash * f) / step) * step) })).filter((q) => q.value <= max),
    };
  }
  openInvestSliderModal({
    title: title.replace(/<[^>]+>/g, ""), description: [label, contextInfo].filter(Boolean).join(" "),
    ...cfg, confirmLabel: "Confirm",
    computeEffect: (v) => {
      if (pct || months) return { stats: [{ label: pct ? "Change" : "Over", value: pct ? `${v}%` : `${v} month${+v === 1 ? "" : "s"}` }] };
      const after = borrowing ? cash + v : cash - v;
      return {
        stats: [{ label: borrowing ? "Borrowed" : "Spend", value: fmtMoney(v) },
                { label: "Cash after", value: fmtMoney(after), cls: after < 0 ? "neg" : "" },
                ...(borrowing || cash <= 0 ? [] : [{ label: "Share of cash", value: `${Math.round((v / cash) * 100)}%` }])],
        note: !borrowing && after < 0 ? "More than the cash you hold." : "", noteWarn: !borrowing && after < 0,
      };
    },
    onConfirm: onSubmit,
  });
}

function toast(msg, forceWarn, ms) {
  // forceWarn is an optional, purely additive override for callers that
  // already know the outcome (e.g. an endpoint that returned ok=false) and
  // shouldn't have to hope their wording happens to match the regex below.
  const isWarn = !!forceWarn || /insufficient|^no [a-z].*made\b|^no [a-z].*(built|purchased|launched|paid)\b/i.test(msg || "");
  // big moments get their own sound (2026-10-24): the IPO bell, a win for a deal or a team, coins for money in
  const kind = isWarn ? "bad"
    : /IPO complete|listed on the/i.test(msg || "") ? "bell"
      : /^(Signed|DEAL!|🏆|Term sheet signed|Licensed to|Hiring \d|🌍 Engineering hub)|You own \d+%|FINALIZED/i.test(msg || "") ? "win"
        : /dividend|invested .* for \d|grant|in the bank/i.test(msg || "") ? "coin" : "soft";
  sfx(kind);
  const t = document.createElement("div");
  t.className = isWarn ? "toast toast-warn" : "toast";
  t.textContent = isWarn ? `⚠ ${msg}` : msg;
  // stack toasts instead of drawing them on top of each other
  let stack = document.getElementById("toast-stack");
  if (!stack) {
    stack = document.createElement("div");
    stack.id = "toast-stack";
    document.body.appendChild(stack);
  }
  stack.appendChild(t);
  setTimeout(() => { t.remove(); toastFold(stack); }, ms || (isWarn ? 5500 : 3200));
  toastFold(stack);
}

/** Never more than three messages on screen: the older ones fold into one "+N more" line you can open. */
function toastFold(stack) {
  const live = [...stack.querySelectorAll(":scope > .toast")];
  let more = stack.querySelector(":scope > .toast-more");
  const hidden = live.slice(0, Math.max(0, live.length - 3));
  live.forEach((x) => { x.hidden = hidden.includes(x) && !stack.classList.contains("open"); });
  if (!hidden.length) { if (more) more.remove(); stack.classList.remove("open"); return; }
  if (!more) {
    more = document.createElement("button");
    more.type = "button";
    more.className = "toast toast-more";
    more.addEventListener("click", () => { stack.classList.toggle("open"); toastFold(stack); });
    stack.prepend(more);
  }
  more.textContent = stack.classList.contains("open") ? "Show fewer" : `+${hidden.length} more message${hidden.length > 1 ? "s" : ""}`;
}

export { moneySliderMax, openAmountModal, openInvestSliderModal, sliderField, toast };

// Every pop-up gets a visible ✕ (2026-10-24 audit; rule: "every pop-up must fit the screen with a visible close ✕").
// The ✕ presses the pop-up's own way out (Close, Cancel, Not now, Done, Continue), so anything waiting on an
// answer still gets one. Sign-in screens (.guest-start) and pop-ups with no way out are left as they are.
if (typeof document !== "undefined" && typeof MutationObserver !== "undefined") {
  const EXIT = /^(close|cancel|not now|done|continue|later|maybe later|no thanks)\b/i;
  const addX = (back) => {
    if (!back.classList || !back.classList.contains("modal-backdrop") || back.classList.contains("guest-start")) return;
    const modal = back.querySelector(".modal");
    if (!modal || modal.querySelector(".modal-x, .wr-x")) return;
    const exit = [...modal.querySelectorAll("button")].find((b) => EXIT.test((b.textContent || "").trim()));
    if (!exit) return;
    const x = document.createElement("button");
    x.type = "button";
    x.className = "modal-x";
    x.setAttribute("aria-label", "Close");
    x.textContent = "✕";
    x.addEventListener("click", () => exit.click());
    modal.prepend(x);
  };
  new MutationObserver((recs) => recs.forEach((r) => r.addedNodes.forEach((n) => { if (n.nodeType === 1) addX(n); })))
    .observe(document.body || document.documentElement, { childList: true });
}
